import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { fetchCurrentUser } from "../../../utils/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        // sessionStorageから前回のユーザー情報を取得（初期値として使用）
        try {
            const cachedUser = sessionStorage.getItem('cachedUser');
            return cachedUser ? JSON.parse(cachedUser) : null;
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const hasAttemptedLoad = useRef(false); // 初回ロード試行フラグ
    const is403Error = useRef(false); // 403エラーフラグ

    // ユーザー情報を取得
    const loadUser = useCallback(async (force = false) => {
        // ログインページにいる場合は、認証チェックをスキップ（force=trueの場合は除く）
        if (!force && window.location.pathname === '/login') {
            setLoading(false);
            return;
        }

        // 既に403エラーが発生している場合は、再試行しない（force=trueの場合は除く）
        if (!force && is403Error.current) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const currentUser = await fetchCurrentUser();
            setUser(currentUser);
            // 成功したらsessionStorageに保存
            sessionStorage.setItem('cachedUser', JSON.stringify(currentUser));
            is403Error.current = false; // 成功したら403フラグをリセット
        } catch (err) {
            // 403エラーの場合、認証が無効と判断
            if (err?.response?.status === 403) {
                is403Error.current = true; // 403フラグを設定
                // force=trueの場合は、ログインページでもエラーを表示しない
                if (!force || window.location.pathname !== '/login') {
                    console.warn('認証セッションが無効です。ログインが必要です。');
                }
            } else {
                console.error("Failed to load user:", err);
            }
            
            setError(err);
            // エラー時はキャッシュをクリア
            sessionStorage.removeItem('cachedUser');
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // 初回マウント時にユーザー情報を取得（1回のみ）
    useEffect(() => {
        if (!hasAttemptedLoad.current) {
            hasAttemptedLoad.current = true;
            loadUser();
        }
    }, [loadUser]);

    // ログアウト処理
    const logout = useCallback(() => {
        setUser(null);
        setError(null);
        sessionStorage.removeItem('cachedUser');
        is403Error.current = false; // 403フラグをリセット
    }, []);

    // ログイン成功時にユーザー情報を更新
    const setUserAfterLogin = useCallback((userData) => {
        setUser(userData);
        sessionStorage.setItem('cachedUser', JSON.stringify(userData));
        is403Error.current = false; // ログイン成功時に403フラグをリセット
        hasAttemptedLoad.current = false; // 再ロード可能にする
    }, []);

    const value = {
        user,
        loading,
        error,
        loadUser,
        logout,
        setUserAfterLogin,
        isAuthenticated: !!user,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}

